// The syncer: sends the outbox to the server in the background, retries on
// a capped schedule, and never blocks the athlete. One pass sends at most
// OUTBOX_BATCH runs; each run's operations go in order, a redo before the
// answers of the version it opens.

import type { RunResponse } from "../schema/types";
import { RUNS, getAccountRuns, getRun, inTransaction } from "./db";
import type { LocalRun, PendingOp } from "./db";
import { OUTBOX_BATCH, RUN_OUTBOX_MAX, nextDelay } from "./policy";
import type { AssessmentId, AssessmentRun, OnlineSource, SyncLock, Timers, Transport } from "./types";

/**
 * Codes the server answers when the request itself is wrong: sending it
 * again cannot succeed. Every other failure (network, unavailable,
 * unauthenticated while a token refreshes) is worth a retry.
 */
export const PERMANENT_CODES: ReadonlySet<string> = new Set(["invalid-argument", "failed-precondition", "permission-denied"]);

export interface SyncerDeps {
  db: () => Promise<IDBDatabase>;
  transport: Transport;
  timers: Timers;
  online: OnlineSource;
  lock: SyncLock;
  now: () => number;
  /** Runs per pass; OUTBOX_BATCH, and never more. */
  batch?: number;
}

export interface PassResult {
  /** Runs whose outbox the server confirmed in full. */
  sent: number;
  /** Runs that hit a network or server error and wait for a retry. */
  failed: number;
  /** Runs the server refused for good; their operations are parked. */
  refused: number;
  /** Pending runs left out of this pass by the batch bound. */
  remaining: number;
}

/** "functions/invalid-argument" and "invalid-argument" both read "invalid-argument". */
export function errorCode(error: unknown): string {
  const code = typeof error === "object" && error !== null ? (error as { code?: unknown }).code : undefined;
  return typeof code === "string" ? code.replace(/^functions\//, "") : "unknown";
}

function send(transport: Transport, op: PendingOp): Promise<RunResponse> {
  switch (op.kind) {
    case "save":
      return transport.saveAnswers(op.request);
    case "finish":
      return transport.finishRun(op.request);
    case "redo":
      return transport.startRedo(op.request);
  }
}

const versionOf = (op: PendingOp): number => (op.kind === "redo" ? op.request.fromVersion : op.request.version);

async function readRun(db: IDBDatabase, accountId: string, assessmentId: AssessmentId): Promise<LocalRun | undefined> {
  return inTransaction(db, [RUNS], "readonly", (tx) => getRun(tx, accountId, assessmentId));
}

/**
 * The server confirmed `op`. Drop it from the queue unless the athlete
 * changed it while it travelled, and adopt the server run once nothing
 * local is left ahead of it.
 */
function confirm(db: IDBDatabase, accountId: string, assessmentId: AssessmentId, op: PendingOp, server: AssessmentRun) {
  return inTransaction(db, [RUNS], "readwrite", async (tx) => {
    const record = await getRun(tx, accountId, assessmentId);
    if (!record) return;
    if (record.outbox[0]?.rev === op.rev) record.outbox.shift();
    if (record.outbox.length === 0) record.run = server;
    tx.objectStore(RUNS).put(record);
  });
}

/** Parks the run's queue after a permanent refusal: kept on the device, never resent. */
function park(db: IDBDatabase, accountId: string, assessmentId: AssessmentId, code: string, at: number) {
  return inTransaction(db, [RUNS], "readwrite", async (tx) => {
    const record = await getRun(tx, accountId, assessmentId);
    if (!record) return;
    record.refused = { code, at, ops: [...(record.refused?.ops ?? []), ...record.outbox].slice(-RUN_OUTBOX_MAX) };
    record.outbox = [];
    tx.objectStore(RUNS).put(record);
  });
}

type RunOutcome = "sent" | "failed" | "refused";

/** Sends one run's queue in order. Bounded by RUN_OUTBOX_MAX calls. */
async function syncRun(deps: SyncerDeps, db: IDBDatabase, accountId: string, assessmentId: AssessmentId): Promise<RunOutcome> {
  for (let step = 0; step < RUN_OUTBOX_MAX; step += 1) {
    const op = (await readRun(db, accountId, assessmentId))?.outbox[0];
    if (!op) return "sent";
    let reply: RunResponse;
    try {
      reply = await send(deps.transport, op);
    } catch (error) {
      const code = errorCode(error);
      if (!PERMANENT_CODES.has(code)) return "failed";
      await park(db, accountId, assessmentId, code, deps.now());
      // Ids and the code only: an answer is health data and never reaches a log.
      console.error("[data] server refused a run; kept on the device, not retried", {
        accountId,
        assessmentId,
        version: versionOf(op),
        operation: op.kind,
        code,
      });
      return "refused";
    }
    await confirm(db, accountId, assessmentId, op, reply.run);
  }
  // The athlete kept answering while the queue drained; the next kick sends the rest.
  return "sent";
}

/** One pass over the account's outbox: at most `batch` runs, in parallel. */
export async function syncPass(deps: SyncerDeps, db: IDBDatabase, accountId: string): Promise<PassResult> {
  const batch = Math.min(deps.batch ?? OUTBOX_BATCH, OUTBOX_BATCH);
  const records = await inTransaction(db, [RUNS], "readonly", (tx) => getAccountRuns(tx, accountId));
  const pending = records.filter((record) => record.outbox.length > 0);
  const chosen = pending.slice(0, batch);
  const outcomes = await Promise.all(chosen.map((record) => syncRun(deps, db, accountId, record.assessmentId)));
  const count = (outcome: RunOutcome) => outcomes.filter((value) => value === outcome).length;
  return { sent: count("sent"), failed: count("failed"), refused: count("refused"), remaining: pending.length - chosen.length };
}

const FAILED_PASS: PassResult = { sent: 0, failed: 1, refused: 0, remaining: 0 };

/**
 * Schedules passes for the signed-in account. A kick starts a pass unless
 * one runs (it then runs once more after) or a retry is already scheduled
 * (that retry carries the new answer). The `online` event retries at once
 * and restarts the schedule.
 */
export class Syncer {
  private accountId: string | null = null;
  private attempt = 0;
  private timer: unknown = null;
  private running: Promise<PassResult> | null = null;
  private rerun: "kick" | "online" | null = null;
  /** Bumped by reset: a pass of an older generation changes nothing when it ends. */
  private generation = 0;
  private readonly unsubscribe: () => void;

  constructor(private readonly deps: SyncerDeps) {
    this.unsubscribe = deps.online(() => this.onOnline());
  }

  /** Something was queued for `accountId`: send it soon, without waiting on it. */
  kick(accountId: string): void {
    this.follow(accountId);
    if (this.running) {
      this.rerun ??= "kick";
      return;
    }
    if (this.timer !== null) return;
    void this.start();
  }

  /** Runs a pass now, after the one in flight if any; resolves with its result. */
  async flush(accountId: string): Promise<PassResult> {
    this.follow(accountId);
    if (this.running) await this.running;
    this.clearTimer();
    return this.running ?? this.start();
  }

  /** Resolves once no pass is running. */
  async whenIdle(): Promise<void> {
    while (this.running) await this.running;
  }

  /** Forgets the account and its schedule; a pass in flight finishes without effect on it. */
  reset(): void {
    this.clearTimer();
    this.accountId = null;
    this.attempt = 0;
    this.rerun = null;
    this.running = null;
    this.generation += 1;
  }

  dispose(): void {
    this.reset();
    this.unsubscribe();
  }

  private follow(accountId: string): void {
    if (this.accountId !== accountId) {
      this.reset();
      this.accountId = accountId;
    }
  }

  private onOnline(): void {
    if (this.accountId === null) return;
    this.clearTimer();
    this.attempt = 0;
    if (this.running) this.rerun = "online";
    else void this.start();
  }

  private start(): Promise<PassResult> {
    const accountId = this.accountId;
    if (accountId === null) return Promise.resolve({ sent: 0, failed: 0, refused: 0, remaining: 0 });
    const generation = this.generation;
    const pass = this.deps
      .lock(async () => syncPass(this.deps, await this.deps.db(), accountId))
      .catch((error: unknown) => {
        console.error("[data] sync pass failed", { accountId, code: errorCode(error) });
        return FAILED_PASS;
      })
      .then((result) => {
        if (generation === this.generation) {
          this.running = null;
          this.after(result);
        }
        return result;
      });
    this.running = pass;
    return pass;
  }

  private after(result: PassResult): void {
    const rerun = this.rerun;
    this.rerun = null;
    if (rerun === "online" || (rerun === "kick" && result.failed === 0)) {
      void this.start();
    } else if (result.failed > 0) {
      const delay = nextDelay(this.attempt);
      this.attempt += 1;
      console.warn(`[data] ${result.failed} run(s) not sent; retry in ${delay} ms`);
      this.schedule(delay);
    } else {
      this.attempt = 0;
      if (result.remaining > 0) this.schedule(0);
    }
  }

  private schedule(delay: number): void {
    this.timer = this.deps.timers.setTimeout(() => {
      this.timer = null;
      if (!this.running) void this.start();
    }, delay);
  }

  private clearTimer(): void {
    if (this.timer !== null) this.deps.timers.clearTimeout(this.timer);
    this.timer = null;
  }
}
